import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client from "../../../../api/client";
import { GlobalContext } from "../../../../context";
import { fmtRange, fmtTime, greeting, zone } from "../../home/time";
import Icon from "../Icon";
import { classAction, openClass } from "./StaffLivePage";
import { Avatar, Bar, Pill } from "./staffUi";

const KEY = "e1x-staff-scope";
const pctOf = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const name = (c) => (c.cohort.startsWith(c.course) ? c.cohort : `${c.course} · ${c.cohort}`);
const isToday = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: zone() }) === new Date().toLocaleDateString("en-CA", { timeZone: zone() });

/**
 * Admin and instructor Home: today's classes (Start, for admins and the class's instructors), the courses
 * running now and how they're doing, students at risk, work waiting to be graded, and, for admins,
 * what needs setting up. Instructors only see the courses they teach.
 */
export default function StaffHome() {
  const navigate = useNavigate();
  const { authState: { data: auth } } = useContext(GlobalContext);
  const user = auth?.user;
  const [home, setHome] = useState(null);
  const [failed, setFailed] = useState(false);
  const [classes, setClasses] = useState(null);

  useEffect(() => {
    client.get("/staff/home").then((r) => setHome(r.data)).catch(() => setFailed(true));
    const loadClasses = () => client.get("/staff/live", { params: { week: 0 } }).then((r) => setClasses(r.data.classes)).catch(() => setClasses([]));
    loadClasses();
    const t = setInterval(loadClasses, 60000); // Start/Join open 30 minutes before class
    return () => clearInterval(t);
  }, []);

  // Open a staff page on one course (the pages remember the course they show).
  const openCourse = (c, path) => {
    try { localStorage.setItem(KEY, JSON.stringify({ cohort_id: c.cohort_id, course_id: c.course_id })); } catch { /* storage blocked */ }
    navigate(path);
  };

  const admin = home ? home.admin : !!user?.is_admin;
  const today = (classes || []).filter((c) => isToday(c.start) && c.state !== "ended");
  const nextUp = (classes || []).find((c) => c.state !== "ended");
  const t = home?.totals;
  const dateLine = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: zone() });

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div>
          <span className="sp-kicker">{admin ? "Admin" : "Instructor"} · {dateLine}</span>
          <h1>{greeting()}, {user?.firstname || "there"}</h1>
          <p>{admin ? "How every running course is doing, and what needs you today." : "Your classes, your students and the work waiting for you."}</p>
        </div>
      </div>

      {failed ? <section className="complete-box"><div><strong>We couldn't load your Home</strong><small>Refresh the page to try again.</small></div></section>
        : !home ? <><div className="skeleton" style={{ height: 110 }} /><div className="skeleton" style={{ height: 300 }} /></>
        : (
          <>
            <div className="sp-mini sh-tiles">
              <div><b>{t.students}</b><span>Students in {t.courses} running course{t.courses === 1 ? "" : "s"}</span></div>
              <button type="button" onClick={() => navigate("/dashboard/staff/progress")}><b className={t.risk ? "lo" : ""}>{t.risk}</b><span>At risk · {t.watch} to watch</span></button>
              <button type="button" onClick={() => navigate(admin ? "/dashboard/staff/progress" : "/dashboard/instructor/submissions")}><b>{t.to_grade}</b><span>Waiting to be graded</span></button>
              {admin
                ? <button type="button" onClick={() => navigate("/dashboard/staff/certificates")}><b className={t.ready ? "ok" : ""}>{t.ready}</b><span>Ready for a certificate</span></button>
                : <div><b>{nextUp ? fmtTime(nextUp.start) : "—"}</b><span>{nextUp ? `Next class · ${nextUp.topic}` : "No more classes this week"}</span></div>}
            </div>

            <div className="sp-two sh-two">
              <section className="sp-box">
                <div className="sp-box-h"><h2>Today's classes</h2><Link className="link-btn" to={admin ? "/dashboard/staff/live" : "/dashboard/staff/plans"}>{admin ? "Live classes ›" : "My classes ›"}</Link></div>
                <div className="sp-rows">
                  {classes === null ? <div><span /><small>Loading…</small><span /></div>
                    : today.length ? today.map((c) => (
                      <div key={c.id}>
                        <span className="sp-fico big"><Icon name="video" /></span>
                        <div><strong>{c.topic}{c.makeup ? " · makeup" : ""}</strong><small>{c.course} · {fmtRange(c.start, c.end)}</small></div>
                        <span className="sp-acts">
                          {c.state === "live" && <Pill cls="ok">Live now</Pill>}
                          {(() => {
                            const a = classAction(c);
                            return <>
                              {a.enabled && a.note && <span className="course-tag" style={{ fontSize: 12.5 }}>{a.note}</span>}
                              <button className="cbtn go" type="button" disabled={!a.enabled} onClick={() => openClass(c.id, a.kind)}>{a.enabled ? a.label : "Start opens 30 min before"}</button>
                            </>;
                          })()}
                        </span>
                      </div>
                    )) : <div><span /><small>No classes today.{nextUp ? ` Next: ${nextUp.topic}, ${new Date(nextUp.start).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: zone() })} ${fmtTime(nextUp.start)}.` : ""}</small><span /></div>}
                </div>
              </section>

              <section className="sp-box">
                <div className="sp-box-h"><h2>{admin ? "Needs attention" : "Students to check on"}</h2></div>
                <div className="sp-rows">
                  {admin && home.needs.slice(0, 4).map((n, i) => (
                    <div key={i}>
                      <span className={`sp-fico ${n.kind === "certificates" ? "ok" : ""}`}><Icon name={n.kind === "certificates" ? "award" : "cal"} size={16} /></span>
                      <div><strong style={{ fontWeight: 500 }}>{n.text}</strong></div>
                      <button type="button" className="link-btn" onClick={() => openCourse(n, n.kind === "certificates" ? "/dashboard/staff/certificates" : `/dashboard/staff/plans/${n.cohort_id}/${n.course_id}`)}>{n.kind === "certificates" ? "Review" : "Create plan"}</button>
                    </div>
                  ))}
                  {home.at_risk.slice(0, admin ? 4 : 6).map((s) => (
                    <div key={`${s.id}-${s.course_id}`}>
                      <Avatar s={s} />
                      <div><strong>{s.name}</strong><small>{s.course} · {s.reasons.join(", ") || "at risk"}</small></div>
                      <button type="button" className="link-btn" onClick={() => openCourse(s, "/dashboard/staff/progress")}>Open</button>
                    </div>
                  ))}
                  {!home.at_risk.length && !(admin && home.needs.length) && <div><span /><small>Nothing needs you right now. Nobody is at risk.</small><span /></div>}
                </div>
                {(t.risk > (admin ? 4 : 6) || (admin && home.needs.length > 4)) && (
                  <div className="sp-foot"><button type="button" className="link-btn" onClick={() => navigate("/dashboard/staff/progress")}>See all {t.risk} at-risk students in Student progress ›</button></div>
                )}
              </section>
            </div>

            <section className="sp-box">
              <div className="sp-box-h"><h2>{admin ? "Running courses" : "My courses"}</h2><small>Classes in the last 3 weeks or the next 4</small></div>
              <div className="sp-table-wrap"><table className="sp-table">
                <thead><tr><th>Course</th><th>Week</th><th className="num">Students</th><th>Attendance</th><th>Activities</th><th>Status</th><th className="num">To grade</th><th /></tr></thead>
                <tbody>
                  {home.courses.map((c) => (
                    <tr key={`${c.cohort_id}-${c.course_id}`} className="click" tabIndex={0} onClick={() => openCourse(c, "/dashboard/staff/progress")} onKeyDown={(e) => e.key === "Enter" && openCourse(c, "/dashboard/staff/progress")}>
                      <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{name(c)}</strong>{c.next_class && <div className="course-tag" style={{ fontSize: 12.5 }}>Next class {new Date(c.next_class.start).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: zone() })}, {fmtTime(c.next_class.start)}</div>}</td>
                      <td>{c.week || "—"}{!c.has_plan && <div className="course-tag" style={{ fontSize: 12 }}>No plan yet</div>}</td>
                      <td className="num">{c.students}</td>
                      <td><Bar value={c.attendance} /></td>
                      <td><Bar value={c.activity} /></td>
                      <td>
                        <div className="sp-split" title={`${c.ok} on track · ${c.watch} watch · ${c.risk} at risk`}>
                          <i className="ok" style={{ width: `${pctOf(c.ok, c.students)}%` }} /><i className="watch" style={{ width: `${pctOf(c.watch, c.students)}%` }} /><i className="risk" style={{ width: `${pctOf(c.risk, c.students)}%` }} />
                        </div>
                        <small className="course-tag" style={{ fontSize: 12 }}>{c.risk ? <span style={{ color: "#c0392b" }}>{c.risk} at risk</span> : "Nobody at risk"}</small>
                      </td>
                      <td className="num" style={c.to_grade ? { color: "var(--brand)", fontWeight: 600 } : undefined}>{c.to_grade}</td>
                      <td className="num"><span className="link-btn">Open ›</span></td>
                    </tr>
                  ))}
                  {!home.courses.length && <tr><td colSpan={8} className="course-tag">{admin ? "No course has classes in the last 3 weeks or the next 4." : "None of your courses has classes in the last 3 weeks or the next 4."}</td></tr>}
                </tbody>
              </table></div>
            </section>

            <div className="sh-links">
              {(admin
                ? [["Course plans", "/dashboard/staff/plans", "cal"], ["Student progress", "/dashboard/staff/progress", "chart"], ["Capstone & teams", "/dashboard/staff/capstone", "users"], ["Certificates", "/dashboard/staff/certificates", "award"], ["Live classes", "/dashboard/staff/live", "video"], ["Leaderboard", "/dashboard/staff/leaderboard", "trophy"]]
                : [["My classes", "/dashboard/staff/plans", "book"], ["My students", "/dashboard/staff/progress", "chart"], ["Submissions", "/dashboard/instructor/submissions", "task"], ["Capstone & teams", "/dashboard/staff/capstone", "users"], ["Leaderboard", "/dashboard/staff/leaderboard", "trophy"]]
              ).map(([l, to, icon]) => <Link key={to} to={to} className="sh-link"><Icon name={icon} size={18} /><span>{l}</span></Link>)}
            </div>
          </>
        )}
    </div></div></div>
  );
}
